import { useState } from 'react';
import { api, errorMessage } from '../../lib/api.js';
import { useMedia, useQuota, useInvalidate, useMutation, type Media } from '../../lib/queries.js';
import {
  Button, Card, ConfirmDialog, EmptyState, ErrorBox, Input, Loading, Modal,
  PageHeader, Pagination, Select, CopyButton, useToast,
  IconMedia,
} from '../../components/ui/index.js';
import { bytes, date, num } from '../../lib/format.js';
import { DropZone, MediaGrid, useUploader } from './MediaPicker.js';
import { hasRole, useRole } from '../../lib/auth.js';

export function MediaLibraryPage() {
  const [filters, setFilters] = useState({ page: 1, type: '', q: '' });
  const media = useMedia({ ...filters, perPage: 24 });
  const quota = useQuota();
  const { upload, uploading } = useUploader();
  const [selected, setSelected] = useState<Media | null>(null);
  const role = useRole();

  const usedPct = quota.data
    ? Math.min(100, Math.round((quota.data.storageMb.used / Math.max(1, quota.data.storageMb.max)) * 100))
    : 0;

  return (
    <>
      <PageHeader
        title="คลังไฟล์"
        description={quota.data
          ? `ใช้พื้นที่ ${num(quota.data.storageMb.used)} MB จาก ${num(quota.data.storageMb.max)} MB (${usedPct}%)`
          : undefined}
      />

      {quota.data && (
        <div className="mb-5 h-1.5 overflow-hidden rounded-full bg-[var(--sunken)]">
          <div className={usedPct > 90 ? 'h-full bg-rose-500' : usedPct > 70 ? 'h-full bg-amber-500' : 'h-full bg-[var(--brand)]'}
               style={{ width: `${usedPct}%` }} />
        </div>
      )}

      <div className="space-y-5">
        {hasRole(role, 'AUTHOR') && <DropZone onFiles={upload} uploading={uploading} />}

        <div className="flex flex-wrap gap-3">
          <div className="min-w-[220px] flex-1">
            <Input placeholder="ค้นหาจากชื่อไฟล์…" value={filters.q}
                   onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value, page: 1 }))} />
          </div>
          <Select value={filters.type} className="w-auto"
                  onChange={(e) => setFilters((f) => ({ ...f, type: e.target.value, page: 1 }))}>
            <option value="">ทุกชนิดไฟล์</option>
            <option value="image">รูปภาพ</option>
            <option value="pdf">PDF</option>
          </Select>
        </div>

        {media.isLoading ? <Loading /> : media.error ? (
          <ErrorBox error={media.error} onRetry={() => void media.refetch()} />
        ) : !media.data?.data.length ? (
          <EmptyState icon={<IconMedia size={22} />} title="ยังไม่มีไฟล์ในคลัง"
                      description="ลากไฟล์มาวางด้านบนเพื่ออัปโหลดไฟล์แรก" />
        ) : (
          <>
            <MediaGrid items={media.data.data} selectedId={selected?.id} onSelect={setSelected} />
            <Pagination page={media.data.meta.page} totalPages={media.data.meta.totalPages}
                        onChange={(page) => setFilters((f) => ({ ...f, page }))} />
          </>
        )}
      </div>

      {selected && <MediaDetail media={selected} onClose={() => setSelected(null)} />}
    </>
  );
}

function MediaDetail({ media, onClose }: { media: Media; onClose: () => void }) {
  const invalidate = useInvalidate();
  const toast = useToast();
  const role = useRole();
  const [altText, setAltText] = useState(media.altText ?? '');
  const [deleting, setDeleting] = useState(false);

  const save = useMutation({
    mutationFn: () => api.patch(`/v1/media/${media.id}`, { altText: altText || null }),
    onSuccess: () => { invalidate(['media']); toast('บันทึกคำอธิบายรูปแล้ว'); },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/v1/media/${media.id}`),
    onSuccess: () => { invalidate(['media'], ['quota']); toast('ลบไฟล์แล้ว'); onClose(); },
    onError: (err) => toast(errorMessage(err), 'error'),
  });

  return (
    <Modal open onClose={onClose} title={media.originalName} size="lg">
      <div className="space-y-4">
        {media.mimeType.startsWith('image/') && (
          <img src={media.publicUrl} alt={media.altText ?? ''}
               className="max-h-[380px] w-full rounded-xl bg-[var(--sunken)] object-contain" />
        )}

        <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
          {[
            ['ขนาดไฟล์', bytes(media.sizeBytes)],
            ['ชนิด', media.mimeType],
            ['ความละเอียด', media.width ? `${media.width}×${media.height}` : '—'],
            ['อัปโหลดเมื่อ', date(media.createdAt)],
          ].map(([label, value]) => (
            <div key={label}>
              <dt className="text-xs text-[var(--ink-3)]">{label}</dt>
              <dd className="mt-0.5 truncate font-medium text-[var(--ink)]">{value}</dd>
            </div>
          ))}
        </dl>

        <Input
          label="คำอธิบายรูป (alt text)" value={altText} onChange={(e) => setAltText(e.target.value)}
          hint="สำคัญต่อ SEO และผู้ใช้ screen reader — อธิบายว่าในรูปมีอะไร"
          maxLength={255}
        />

        <div>
          <span className="label">URL ของไฟล์</span>
          <div className="flex gap-2">
            <input readOnly value={media.publicUrl} onFocus={(e) => e.target.select()}
                   className="min-w-0 flex-1 rounded-lg border border-[var(--line)] bg-[var(--sunken)] px-3 py-2 font-mono text-xs" />
            <CopyButton text={media.publicUrl} />
          </div>
        </div>

        <div className="flex justify-between gap-2 pt-1">
          {hasRole(role, 'EDITOR') && (
            <Button variant="ghost" className="text-[var(--bad)]" onClick={() => setDeleting(true)}>ลบไฟล์</Button>
          )}
          <Button loading={save.isPending} disabled={altText === (media.altText ?? '')}
                  onClick={() => save.mutate()}>บันทึก</Button>
        </div>
      </div>

      <ConfirmDialog
        open={deleting} onClose={() => setDeleting(false)} onConfirm={() => remove.mutate()}
        loading={remove.isPending} danger confirmLabel="ลบไฟล์" title="ลบไฟล์นี้?"
        message="ถ้าไฟล์นี้ถูกใช้เป็นรูปหน้าปกของบทความอยู่ ระบบจะไม่ยอมให้ลบจนกว่าจะเปลี่ยนรูปในบทความนั้นก่อน"
      />
    </Modal>
  );
}
